import { API_URL } from 'env';
import { mixLogOut } from '../services/mixin';

/** Thẻ trạng thái rút gọn đi kèm mỗi bước chuyển (from_tag / to_tag) */
export interface TransitionTagBrief {
    id: number;
    name: string;
    color?: string | null;
    code?: string | null;
}

/** Một bước chuyển: từ from_tag_id sang to_tag_id, trong phạm vi phòng ban + màn hình */
export interface AppointmentTransitionItem {
    id: number;
    department_type: number | null;
    screen_context: number;
    from_tag_id: number;
    to_tag_id: number;
    /** 1 = cho phép chuyển, 0 = chặn */
    is_allowed: 0 | 1;
    created_at?: string;
    updated_at?: string;
    from_tag?: TransitionTagBrief | null;
    to_tag?: TransitionTagBrief | null;
}

export interface TransitionResponse<T> {
    status: boolean;
    data: T | null;
    msg?: string;
}

export interface TransitionScope {
    /** ID phòng ban áp dụng, null = cấu hình dùng chung */
    department_type?: number | null;
    /** 1 = Lịch hẹn, 2 = Lịch tư vấn, 3 = Lịch thực hiện, 4 = Khách hàng (danh sách + chi tiết) */
    screen_context: number;
}

export interface BulkSyncRule {
    from_tag_id: number;
    to_tag_id: number;
    is_allowed: 0 | 1;
}

const BASE = `${API_URL}/api/v1/be/appointment-transitions`;

function authHeaders(): HeadersInit {
    const token = localStorage.getItem('token') || '';
    return {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
    };
}

async function request<T>(
    url: string,
    method: string,
    body?: unknown,
    failMsg = 'Có lỗi xảy ra'
): Promise<TransitionResponse<T>> {
    try {
        const res = await fetch(url, {
            method,
            headers: authHeaders(),
            body: body === undefined ? undefined : JSON.stringify(body),
        });

        if (!res.ok) {
            if (res.status === 401) mixLogOut();
            const errBody = await res.json().catch(() => null);
            return {
                status: false,
                data: null,
                msg: errBody?.msg || `${failMsg} (HTTP ${res.status})`,
            };
        }

        return (await res.json().catch(() => ({ status: true, data: null }))) as TransitionResponse<T>;
    } catch (error) {
        console.error(`Failed ${method} ${url}:`, error);
        return { status: false, data: null, msg: 'Lỗi kết nối tới máy chủ' };
    }
}

/** GET /appointment-transitions — lấy ma trận theo phòng ban / màn hình */
export async function getAppointmentTransitions(params?: {
    department_type?: number | null;
    screen_context?: number;
    from_tag_id?: number;
}): Promise<TransitionResponse<AppointmentTransitionItem[]>> {
    const query = new URLSearchParams();
    if (params?.department_type !== undefined && params.department_type !== null) {
        query.append('department_type', String(params.department_type));
    }
    if (params?.screen_context) query.append('screen_context', String(params.screen_context));
    if (params?.from_tag_id) query.append('from_tag_id', String(params.from_tag_id));
    const qs = query.toString();

    return request<AppointmentTransitionItem[]>(
        `${BASE}${qs ? `?${qs}` : ''}`,
        'GET',
        undefined,
        'Không tải được ma trận chuyển trạng thái'
    );
}

/** POST /appointment-transitions/add-from-tag — thêm một hàng trạng thái nguồn kèm nhiều đích */
export async function addTransitionFromTag(payload: TransitionScope & {
    from_tag_id: number;
    to_tag_ids?: number[];
    is_allowed?: 0 | 1;
}): Promise<TransitionResponse<AppointmentTransitionItem[]>> {
    return request<AppointmentTransitionItem[]>(
        `${BASE}/add-from-tag`,
        'POST',
        payload,
        'Thêm trạng thái nguồn thất bại'
    );
}

/** POST /appointment-transitions — thêm một bước chuyển đích */
export async function createTransition(payload: TransitionScope & {
    from_tag_id: number;
    to_tag_id: number;
    is_allowed?: 0 | 1;
}): Promise<TransitionResponse<AppointmentTransitionItem>> {
    return request<AppointmentTransitionItem>(BASE, 'POST', payload, 'Thêm bước chuyển thất bại');
}

/** PATCH /appointment-transitions/{id}/toggle — bỏ trống is_allowed thì server tự đảo trạng thái */
export async function toggleTransition(
    id: number,
    isAllowed?: 0 | 1
): Promise<TransitionResponse<AppointmentTransitionItem>> {
    return request<AppointmentTransitionItem>(
        `${BASE}/${id}/toggle`,
        'PATCH',
        isAllowed === undefined ? {} : { is_allowed: isAllowed },
        'Đổi trạng thái bước chuyển thất bại'
    );
}

/** DELETE /appointment-transitions/{id} — xóa một bước chuyển đích */
export async function deleteTransition(id: number): Promise<TransitionResponse<null>> {
    return request<null>(`${BASE}/${id}`, 'DELETE', undefined, 'Xóa bước chuyển thất bại');
}

/** DELETE /appointment-transitions/remove-from-tag — xóa cả hàng của một trạng thái nguồn */
export async function removeTransitionFromTag(
    payload: TransitionScope & { from_tag_id: number }
): Promise<TransitionResponse<null>> {
    return request<null>(
        `${BASE}/remove-from-tag`,
        'DELETE',
        payload,
        'Xóa hàng trạng thái thất bại'
    );
}

/** POST /appointment-transitions/bulk-sync — lưu cả ma trận trong một transaction */
export async function bulkSyncTransitions(
    payload: TransitionScope & { transitions: BulkSyncRule[] }
): Promise<TransitionResponse<AppointmentTransitionItem[]>> {
    return request<AppointmentTransitionItem[]>(
        `${BASE}/bulk-sync`,
        'POST',
        payload,
        'Lưu ma trận thất bại'
    );
}

/** POST /appointment-transitions/reset-master — nạp lại luồng chuẩn của Admin */
export async function resetMasterTransitions(
    payload: TransitionScope
): Promise<TransitionResponse<null>> {
    return request<null>(
        `${BASE}/reset-master`,
        'POST',
        payload,
        'Khôi phục luồng gốc thất bại'
    );
}
